import { Button } from "@gigpro/ui/Button";
import {
  DialogSelectBaseTrigger,
  DialogSelectDialog,
  DialogSelectItem,
  DialogSelectRoot,
  DialogSelectTrigger,
} from "@gigpro/ui/DialogSelect";
import { Delete, Edit } from "@gigpro/ui/Icons";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { cn } from "@gigpro/ui/tailwind/cn";
import { toast } from "@gigpro/ui/Toast";
import { formatCurrency } from "@gigpro/utils";
import { useStafferGigsTemplatesDestroy, type ForManagerLocationDetail } from "@sidegigllc/api/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getTipPolicyLabel } from "api/resources/shared";
import { DeleteConfirmationDialog } from "components/DeleteConfirmationDialog";
import { gigTemplatesQuery } from "domains/postGig/containers/PositionSelect/queries";
import type { Pro, SectionPros, SectionShift } from "domains/postGig/providers/PostGigData/types";
import { useFeatureFlag } from "providers/LaunchDarkly";
import React from "react";
import * as R from "remeda";
import { toastApiError } from "utils/toastApiError";
import { FormDialog } from "../ProsStep/FormDialog";

type PositionSelectProps = {
  className?: string;
  error?: string;

  shift: SectionShift;
  value?: SectionPros;
  onChange: (data: SectionPros) => void;
  location: ForManagerLocationDetail;

  children?: React.ReactNode;

  mode?: "single" | "multiple";
  /** Request a Pro saves the position and picks it, and the picker closes with no Apply step. */
  closeAfterSave?: boolean;
  /** The one-off positions are the value's own, never a growing list of what it once held (a page keeps its draft in `value`). */
  currentOneOffsOnly?: boolean;
  flags?: React.ComponentProps<typeof FormDialog>["flags"] & { disableDefaults?: boolean };
};

const oneOffProIdPrefix = "one-off-pro";

/** A row's tips answer: kept whole while a long position or template name truncates beside it. */
const TipsAnswer = ({ children }: Readonly<{ children: React.ReactNode }>) => (
  <span className="shrink-0 font-regular text-gray">· {children}</span>
);

const getValue = (selectedEntry: SectionPros[number] | undefined, oneOffPositions: SectionPros) => {
  if (!selectedEntry?.pro) return "";
  if (selectedEntry.pro.id) return String(selectedEntry.pro.id);
  const { hourly_rate, ...selectedProWithoutWage } = selectedEntry.pro;
  return `${oneOffProIdPrefix}_${oneOffPositions.findIndex(({ pro: { hourly_rate, ...pro } }) => R.isDeepEqual(pro, selectedProWithoutWage))}`;
};

export function PositionSelect({
  className,
  error,
  shift,
  value = [],
  onChange,
  flags,
  location,

  mode = "single",
  closeAfterSave,
  currentOneOffsOnly,
  children,
}: Readonly<PositionSelectProps>) {
  const matchV3Web = useFeatureFlag("match-v3-web");
  const isIncludesTipsEnabled = useFeatureFlag("fe-shift-includes-tips");
  const autoSelectToggleFlag = useFeatureFlag("temp-show-gig-autoselect-toggle");
  const showAutoSelect =
    !matchV3Web && autoSelectToggleFlag && !!location.immediate_auto_select_enabled && !!flags?.showAutoSelect;

  const queryClient = useQueryClient();
  const templatesQuery = gigTemplatesQuery({ location });
  const { data: templates = [], isLoading: loading } = useQuery(templatesQuery);
  const invalidateTemplates = () => queryClient.invalidateQueries({ queryKey: templatesQuery.queryKey });
  // Templates the delete request has removed but the refetch hasn't dropped yet.
  // Excluding them keeps a just-deleted template from being re-selected as the default.
  const [deletedTemplateIds, setDeletedTemplateIds] = React.useState<ReadonlySet<number>>(new Set());
  const availableTemplates = templates.filter((t) => !deletedTemplateIds.has(t.id));
  const [keptOneOffs, setKeptOneOffs] = React.useState<SectionPros>(value.filter((t) => !t?.pro?.id));
  const newOneOffPositions = value.filter((t) => !t.pro.id && !keptOneOffs.some((p) => R.isDeepEqual(p.pro, t.pro)));
  if (!currentOneOffsOnly && newOneOffPositions.length) setKeptOneOffs([...keptOneOffs, ...newOneOffPositions]);
  const oneOffPositions = currentOneOffsOnly ? value.filter((t) => !t.pro.id) : keptOneOffs;

  const [formDialogOpen, setFormDialogOpen] = React.useState(false);
  // Remounting the picker closes it: its open state is its own.
  const [pickerKey, setPickerKey] = React.useState(0);
  const [activePro, setActivePro] = React.useState<SectionPros[number]>();

  const [templateToDelete, setTemplateToDelete] = React.useState<Pro>();
  const deleteTemplate = useStafferGigsTemplatesDestroy({
    mutation: {
      onSuccess: (_data, { id }) => {
        toast.success("Template deleted");
        setDeletedTemplateIds((prev) => new Set(prev).add(id));
        invalidateTemplates();
        onChange(value.filter((entry) => entry.pro.id !== id));
      },
      onError: (error) => {
        toastApiError(error);
      },
    },
  });

  const selectedProValue = value[0];
  const defaultTemplate = availableTemplates[0];
  React.useEffect(() => {
    if (flags?.disableDefaults || selectedProValue || !defaultTemplate) return;
    onChange([{ count: 1, pro: defaultTemplate }]);
  }, [selectedProValue, defaultTemplate, flags?.disableDefaults, onChange]);

  const selectValue =
    mode === "single"
      ? { mode, value: getValue(selectedProValue, oneOffPositions) }
      : { mode, value: value.map((entry) => getValue(entry, oneOffPositions)) };

  const onValueChange = (newValue: string | Array<string>) => {
    if (Array.isArray(newValue)) {
      onChange(
        newValue
          .map((newPosition) => {
            if (newPosition.startsWith(oneOffProIdPrefix)) {
              const positionIndex = Number(newPosition.slice(newPosition.indexOf("_") + 1));
              return oneOffPositions[positionIndex];
            }
            const selectedTemplateId = Number(newPosition);
            const template = availableTemplates.find(({ id }) => id === selectedTemplateId);
            const count = value.find(({ pro }) => pro.id === selectedTemplateId)?.count ?? 1;
            return template ? { count, pro: template } : undefined;
          })
          .filter((v) => !!v),
      );
      return;
    }
    if (newValue.startsWith(oneOffProIdPrefix)) {
      const positionIndex = Number(newValue.slice(newValue.indexOf("_") + 1));
      onChange([oneOffPositions[positionIndex]]);
      return;
    }
    const selectedTemplateId = Number(newValue);
    const template = availableTemplates.find(({ id }) => id === selectedTemplateId);
    if (template && selectedProValue?.pro.id !== selectedTemplateId) {
      onChange([{ count: selectedProValue?.count ?? 1, pro: template }]);
    }
  };

  if (loading) return <Skeleton className={cn("h-14.5 w-full scale-y-60 self-center", className)} />;
  return (
    <>
      <DialogSelectRoot key={pickerKey} {...selectValue} onValueChange={onValueChange}>
        {children ? (
          <DialogSelectBaseTrigger className={className} asChild>
            {children}
          </DialogSelectBaseTrigger>
        ) : (
          <DialogSelectTrigger
            className={className}
            label="Position needed"
            disabled={!shift?.startTime}
            placeholder={availableTemplates.length === 0 ? "Add a position" : "Select position"}
            error={error}
          />
        )}
        <DialogSelectDialog title="Positions" action="Add a position" onAction={() => setFormDialogOpen(true)}>
          {!!oneOffPositions.length && (
            <>
              <p className="font-semibold">Positions</p>
              {oneOffPositions.map(({ pro, count }, idx) => (
                <DialogSelectItem
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  value={`${oneOffProIdPrefix}_${idx}`}
                  action={
                    <Button
                      data-testid={`edit-${pro.skill_name}`}
                      icon
                      onClick={() => {
                        setActivePro({ count, pro });
                        setFormDialogOpen(true);
                      }}
                    >
                      <Edit className="size-4" />
                    </Button>
                  }
                >
                  <span className="min-w-0 truncate">
                    {pro.skill_name}{" "}
                    <span className="font-regular">
                      {currentOneOffsOnly && !pro.hourly_rate ? "Pay not set" : `${formatCurrency(pro.hourly_rate)}/hr`}
                    </span>
                  </span>
                  {isIncludesTipsEnabled && getTipPolicyLabel(pro.tip_policy) && (
                    <TipsAnswer>{getTipPolicyLabel(pro.tip_policy)}</TipsAnswer>
                  )}
                </DialogSelectItem>
              ))}
            </>
          )}
          <p className="font-semibold">Select from your templates</p>
          {availableTemplates.map((template) => (
            <DialogSelectItem
              key={template.id}
              value={String(template.id)}
              action={
                <>
                  <Button
                    data-testid={`edit-${template.name}`}
                    icon
                    onClick={() => {
                      setActivePro(value.find(({ pro }) => pro.id === template.id) ?? { count: 1, pro: template });
                      setFormDialogOpen(true);
                    }}
                  >
                    <Edit className="size-4" />
                  </Button>
                  <Button
                    data-testid={`delete-${template.name}`}
                    icon
                    className="text-error"
                    onClick={() => setTemplateToDelete(template)}
                  >
                    <Delete className="size-4" />
                  </Button>
                </>
              }
            >
              <span className="min-w-0 truncate">
                {template.name}{" "}
                <span className="ml-0.5 font-regular">
                  ({template.skill_name}) {formatCurrency(template.hourly_rate)}/hr
                </span>
              </span>
              {isIncludesTipsEnabled && getTipPolicyLabel(template.tip_policy) && (
                <TipsAnswer>{getTipPolicyLabel(template.tip_policy)}</TipsAnswer>
              )}
            </DialogSelectItem>
          ))}
        </DialogSelectDialog>
      </DialogSelectRoot>
      <FormDialog
        flags={{ ...flags, showAutoSelect }}
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setActivePro(undefined);
        }}
        shift={shift}
        location={location}
        template={activePro?.pro}
        onSuccess={(updatedTemplate) => {
          const newEntry = {
            count: activePro?.count ?? 1,
            pro: updatedTemplate,
            wageOverride: activePro?.wageOverride,
            // Keep the per-post answer only while the position's own answer is unchanged; an edit that
            // changes it means the new answer should win, not a stale override.
            tipPolicyOverride:
              updatedTemplate.tip_policy === activePro?.pro.tip_policy ? activePro?.tipPolicyOverride : undefined,
          };
          const prevIndex = value.findIndex((t) => R.isDeepEqual(t, activePro));
          if (prevIndex === -1) {
            onChange(mode === "single" ? [newEntry] : [...value, newEntry]);
          } else {
            onChange(value.toSpliced(prevIndex, 1, newEntry));
          }
          setActivePro(undefined);
          if (closeAfterSave) setPickerKey((key) => key + 1);
        }}
      />
      <DeleteConfirmationDialog
        open={!!templateToDelete}
        onClose={() => setTemplateToDelete(undefined)}
        title="Delete template"
        content={`Are you sure you want to delete "${templateToDelete?.name}"?`}
        isPending={deleteTemplate.isPending}
        onDelete={() => {
          if (!templateToDelete?.id) return;
          deleteTemplate.mutate({ id: templateToDelete.id }, { onSettled: () => setTemplateToDelete(undefined) });
        }}
      />
    </>
  );
}
